import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiRefreshCw, FiEdit2, FiPlus, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  listPolicyRules, upsertPolicyRule,
  listBlacklist, addToBlacklist,
  isExpenseAdmin, errMsg,
} from '../../utils/expenseApi';

const CATEGORIES = [
  '', 'TRAVEL_AIRFARE', 'TRAVEL_HOTEL', 'TRAVEL_GROUND', 'TRAVEL_TRAIN',
  'MEALS', 'MEALS_CLIENT', 'OFFICE_SUPPLIES', 'COMMUNICATION',
  'CONFERENCE', 'FUEL', 'VISA_PASSPORT', 'MEDICAL', 'MISCELLANEOUS',
];

const CATEGORY_LABELS = {
  TRAVEL_AIRFARE: 'Airfare', TRAVEL_HOTEL: 'Hotel',
  TRAVEL_GROUND: 'Ground Transport', TRAVEL_TRAIN: 'Train',
  MEALS: 'Meals', MEALS_CLIENT: 'Client Meals',
  OFFICE_SUPPLIES: 'Office Supplies', COMMUNICATION: 'Communication',
  CONFERENCE: 'Conference', FUEL: 'Fuel',
  VISA_PASSPORT: 'Visa / Passport', MEDICAL: 'Medical',
  MISCELLANEOUS: 'Miscellaneous',
};

const emptyRule = () => ({
  rule_id: '', rule_name: '', category: '',
  company_code: '1000', employee_grade: '',
  amount_limit: '', currency: 'INR',
  per_diem_rate: '', requires_receipt: false,
  receipt_threshold: '', is_active: true,
  effective_date: new Date().toISOString().slice(0, 10),
  expiry_date: '', rule_text: '',
});

function RuleModal({ rule, onSave, onClose }) {
  const [form,    setForm]    = useState(rule || emptyRule());
  const [saving,  setSaving]  = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.rule_id.trim()) { toast.error('Rule ID is required.'); return; }
    if (!form.rule_name.trim()) { toast.error('Rule name is required.'); return; }
    if (!form.category) { toast.error('Category is required.'); return; }

    const payload = {
      ...form,
      amount_limit:        form.amount_limit ? parseFloat(form.amount_limit) : null,
      per_diem_rate:       form.per_diem_rate ? parseFloat(form.per_diem_rate) : null,
      receipt_threshold:   form.receipt_threshold ? parseFloat(form.receipt_threshold) : null,
      expiry_date:         form.expiry_date || null,
    };

    setSaving(true);
    try {
      await upsertPolicyRule(payload);
      toast.success('Policy rule saved.');
      onSave();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to save rule.'));
    }
    setSaving(false);
  };

  return (
    <div className="exp-modal-overlay" onClick={onClose}>
      <motion.div
        className="exp-modal"
        style={{ maxWidth: 640 }}
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 className="exp-modal-title" style={{ margin: 0 }}>
            {rule ? 'Edit Policy Rule' : 'New Policy Rule'}
          </h3>
          <button className="exp-icon-btn" onClick={onClose}><FiX size={14} /></button>
        </div>

        <form onSubmit={handleSave}>
          <div className="exp-rule-form-grid">
            <div className="exp-field">
              <label className="exp-label">Rule ID *</label>
              <input className="exp-input" value={form.rule_id} onChange={e => upd('rule_id', e.target.value)} required placeholder="HOTEL_DOM_B1" disabled={!!rule} />
            </div>
            <div className="exp-field">
              <label className="exp-label">Rule Name *</label>
              <input className="exp-input" value={form.rule_name} onChange={e => upd('rule_name', e.target.value)} required placeholder="Domestic Hotel — Band B1" />
            </div>
            <div className="exp-field">
              <label className="exp-label">Category *</label>
              <select className="exp-select" value={form.category} onChange={e => upd('category', e.target.value)} required>
                <option value="">Select…</option>
                {CATEGORIES.filter(Boolean).map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
              </select>
            </div>
            <div className="exp-field">
              <label className="exp-label">Company Code</label>
              <input className="exp-input" value={form.company_code} onChange={e => upd('company_code', e.target.value)} placeholder="1000" />
            </div>
            <div className="exp-field">
              <label className="exp-label">Employee Grade</label>
              <input className="exp-input" value={form.employee_grade} onChange={e => upd('employee_grade', e.target.value)} placeholder="B1" />
            </div>
            <div className="exp-field">
              <label className="exp-label">Amount Limit</label>
              <input type="number" className="exp-input" value={form.amount_limit} onChange={e => upd('amount_limit', e.target.value)} min="0" step="0.01" placeholder="5000.00" />
            </div>
            <div className="exp-field">
              <label className="exp-label">Currency</label>
              <input className="exp-input" value={form.currency} onChange={e => upd('currency', e.target.value)} placeholder="INR" maxLength={3} />
            </div>
            <div className="exp-field">
              <label className="exp-label">Per Diem Rate</label>
              <input type="number" className="exp-input" value={form.per_diem_rate} onChange={e => upd('per_diem_rate', e.target.value)} min="0" step="0.01" placeholder="—" />
            </div>
            <div className="exp-field">
              <label className="exp-label">Receipt Threshold</label>
              <input type="number" className="exp-input" value={form.receipt_threshold} onChange={e => upd('receipt_threshold', e.target.value)} min="0" step="0.01" placeholder="1000.00" />
            </div>
            <div className="exp-field">
              <label className="exp-label">Effective Date</label>
              <input type="date" className="exp-input" value={form.effective_date} onChange={e => upd('effective_date', e.target.value)} />
            </div>
            <div className="exp-field">
              <label className="exp-label">Expiry Date</label>
              <input type="date" className="exp-input" value={form.expiry_date} onChange={e => upd('expiry_date', e.target.value)} />
            </div>
            <div className="exp-field" style={{ gridColumn: '1 / -1' }}>
              <label className="exp-label">Rule Text</label>
              <textarea className="exp-textarea" rows={2} value={form.rule_text} onChange={e => upd('rule_text', e.target.value)} placeholder="Human-readable description of this rule" />
            </div>
            <div className="exp-field" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="req_receipt" checked={form.requires_receipt} onChange={e => upd('requires_receipt', e.target.checked)} />
              <label htmlFor="req_receipt" className="exp-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', margin: 0 }}>Requires receipt</label>
            </div>
            <div className="exp-field" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => upd('is_active', e.target.checked)} />
              <label htmlFor="is_active" className="exp-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', margin: 0 }}>Active</label>
            </div>
          </div>

          <div className="exp-modal-footer">
            <motion.button type="button" className="exp-btn ghost" onClick={onClose} whileTap={{ scale: 0.96 }}>Cancel</motion.button>
            <motion.button type="submit" className="exp-btn primary" disabled={saving} whileTap={{ scale: 0.96 }}>
              {saving ? <><div className="exp-inline-spinner" /> Saving…</> : 'Save Rule'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function BlacklistTab() {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [form,    setForm]    = useState({ merchant_name: '', reason: '' });
  const [adding,  setAdding]  = useState(false);

  const load = async () => {
    setLoading(true);
    try { setList(await listBlacklist()); } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.merchant_name.trim()) { toast.error('Merchant name is required.'); return; }
    setAdding(true);
    try {
      await addToBlacklist({ merchant_name: form.merchant_name.trim(), reason: form.reason.trim() || undefined });
      toast.success('Merchant added to blacklist.');
      setForm({ merchant_name: '', reason: '' });
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to add merchant.'));
    }
    setAdding(false);
  };

  return (
    <div>
      {isExpenseAdmin() && (
        <form onSubmit={handleAdd}>
          <div className="exp-card" style={{ marginBottom: 14 }}>
            <div className="exp-card-header"><span className="exp-card-title">Add Blacklisted Merchant</span></div>
            <div className="exp-card-body">
              <div className="exp-form-grid">
                <div className="exp-field">
                  <label className="exp-label">Merchant Name *</label>
                  <input className="exp-input" value={form.merchant_name} onChange={e => setForm(f => ({ ...f, merchant_name: e.target.value }))} required placeholder="ShadyTravel.com" />
                </div>
                <div className="exp-field">
                  <label className="exp-label">Reason</label>
                  <input className="exp-input" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Non-compliant vendor" />
                </div>
              </div>
              <motion.button type="submit" className="exp-btn primary small" disabled={adding} whileTap={{ scale: 0.96 }}>
                {adding ? <><div className="exp-inline-spinner" /> Adding…</> : <><FiPlus size={12} /> Add</>}
              </motion.button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>
      ) : list.length === 0 ? (
        <div className="exp-empty"><div className="exp-empty-msg">No blacklisted merchants.</div></div>
      ) : (
        <div className="exp-table-wrap">
          <table className="exp-table">
            <thead>
              <tr><th>Merchant</th><th>Reason</th><th>Added By</th><th>Added At</th><th>Active</th></tr>
            </thead>
            <tbody>
              {list.map(m => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 700 }}>{m.merchant_name}</td>
                  <td>{m.reason || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--e-muted)' }}>{m.added_by?.slice(0, 12)}…</td>
                  <td>{new Date(m.added_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td>{m.is_active ? <span className="exp-badge APPROVED">Active</span> : <span className="exp-badge CANCELLED">Inactive</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function PolicyRules() {
  const [rules,       setRules]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [catFilter,   setCatFilter]   = useState('');
  const [activeOnly,  setActiveOnly]  = useState(true);
  const [modal,       setModal]       = useState(null);
  const [subTab,      setSubTab]      = useState('rules');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (catFilter)     params.category  = catFilter;
      if (activeOnly)    params.is_active = true;
      setRules(await listPolicyRules(params));
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { if (subTab === 'rules') load(); }, [catFilter, activeOnly, subTab]);

  const handleSaved = () => {
    setModal(null);
    load();
  };

  return (
    <motion.div className="exp-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 className="exp-section-title" style={{ margin: 0 }}>Policy</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {subTab === 'rules' && (
            <motion.button className="exp-btn ghost small" onClick={load} whileTap={{ scale: 0.95 }}>
              <FiRefreshCw size={12} />
            </motion.button>
          )}
          {isExpenseAdmin() && subTab === 'rules' && (
            <motion.button className="exp-btn primary small" onClick={() => setModal('new')} whileTap={{ scale: 0.96 }}>
              <FiPlus size={13} /> New Rule
            </motion.button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="exp-report-tabs">
        {[{ id: 'rules', label: 'Policy Rules' }, { id: 'blacklist', label: 'Merchant Blacklist' }].map(t => (
          <button key={t.id} className={`exp-report-tab ${subTab === t.id ? 'active' : ''}`} onClick={() => setSubTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'blacklist' ? <BlacklistTab /> : (
        <>
          {/* Filters */}
          <div className="exp-filter-bar" style={{ marginBottom: 12 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--e-muted)' }}>Category:</span>
            {CATEGORIES.map(c => (
              <motion.button
                key={c || 'all'}
                className={`exp-btn xs ${catFilter === c ? 'primary' : ''}`}
                onClick={() => setCatFilter(c)}
                whileTap={{ scale: 0.96 }}
              >
                {c ? (CATEGORY_LABELS[c] || c) : 'All'}
              </motion.button>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: 'var(--e-muted)' }}>
              <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
              Active only
            </label>
          </div>

          {loading ? (
            <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>
          ) : rules.length === 0 ? (
            <div className="exp-empty">
              <div className="exp-empty-msg">No policy rules found.</div>
            </div>
          ) : (
            <div className="exp-table-wrap">
              <table className="exp-table">
                <thead>
                  <tr>
                    <th>Rule ID</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Grade</th>
                    <th>Limit</th>
                    <th>Requires Receipt</th>
                    <th>Status</th>
                    {isExpenseAdmin() && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {rules.map(r => (
                    <tr key={r.rule_id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--e-muted)' }}>{r.rule_id}</td>
                      <td style={{ fontWeight: 600 }}>
                        <div>{r.rule_name}</div>
                        {r.rule_text && <div style={{ fontSize: '0.75rem', color: 'var(--e-muted)', marginTop: 2 }}>{r.rule_text}</div>}
                      </td>
                      <td>{CATEGORY_LABELS[r.category] || r.category}</td>
                      <td>{r.employee_grade || '—'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--ep)' }}>
                        {r.amount_limit != null
                          ? `${parseFloat(r.amount_limit).toLocaleString('en-IN')} ${r.currency}`
                          : r.per_diem_rate != null ? `${parseFloat(r.per_diem_rate).toLocaleString('en-IN')}/day` : '—'
                        }
                      </td>
                      <td>
                        {r.requires_receipt
                          ? <span className="exp-badge APPROVED">Yes</span>
                          : <span className="exp-badge CANCELLED">No</span>}
                        {r.requires_receipt && r.receipt_threshold != null && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--e-muted)', marginTop: 2 }}>
                            above {parseFloat(r.receipt_threshold).toLocaleString('en-IN')}
                          </div>
                        )}
                      </td>
                      <td>
                        {r.is_active
                          ? <span className="exp-badge APPROVED">Active</span>
                          : <span className="exp-badge CANCELLED">Inactive</span>}
                      </td>
                      {isExpenseAdmin() && (
                        <td>
                          <motion.button
                            className="exp-icon-btn"
                            onClick={() => setModal(r)}
                            whileTap={{ scale: 0.92 }}
                            title="Edit rule"
                          >
                            <FiEdit2 size={13} />
                          </motion.button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <AnimatePresence>
        {modal && (
          <RuleModal
            rule={modal === 'new' ? null : modal}
            onSave={handleSaved}
            onClose={() => setModal(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
